import { useTheme } from "@emotion/react";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faTrash } from "@fortawesome/pro-light-svg-icons";
import {
  DataSource,
  Operator,
  ProviderType,
} from "@ternary/api-lib/analytics/enums";
import { Dimension } from "@ternary/api-lib/analytics/ui/types";
import { DimensionPreferenceEntity } from "@ternary/api-lib/core/types";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import React from "react";
import { z } from "zod";
import { DimensionValuesMap } from "../../../../api/analytics/useGetDimensionValues";
import { operatorOptions } from "../../../../constants";
import Select from "../../../../ui-lib/components/Select";
import SelectCheckbox from "../../../../ui-lib/components/SelectCheckbox";
import { groupOptionsByPreferences } from "../../../../utils/groupOptionsByPreferences";
import copyText from "../../copyText";
import { BillingRuleForm } from "./BillingRuleForm";
import { filterSchema, Form } from "./types";

interface Props {
  availableDimensions: Dimension[];
  dimensionPreferences: DimensionPreferenceEntity[];
  form: Form;
  dimensionValues: DimensionValuesMap;
  isLoadingDimensionPreferences: boolean;
  isLoadingDimensionValues: boolean;
  required?: boolean;
  onInteraction: (interaction: BillingRuleForm.Interaction) => void;
}

export function FilterSection(props: Props) {
  const theme = useTheme();

  const { form } = props;

  //
  // Render
  //

  const dimensionOptions = props.availableDimensions.map(
    (availableDimension) => ({
      label: availableDimension.displayName,
      value: availableDimension.schemaName,
    })
  );

  const groupedFilterOptions = groupOptionsByPreferences(
    dimensionOptions,
    props.dimensionPreferences,
    DataSource.FOCUS_BILLING
  );

  return (
    <Box
      backgroundColor={theme.panel_backgroundColor}
      borderRadius={theme.borderRadius_2}
      padding={theme.space_md}
    >
      <Flex alignItems="center" gap={theme.space_xs}>
        <Text fontSize={theme.fontSize_base}>
          {copyText.filtersSectionLabel}
        </Text>
      </Flex>
      <form.Field
        name="filters"
        validators={{
          onChange: props.required
            ? z.array(filterSchema).min(1)
            : z.array(filterSchema),
          onMount: props.required
            ? z.array(filterSchema).min(1)
            : z.array(filterSchema),
        }}
        mode="array"
        children={(field) => {
          return (
            <Flex direction="column" gap={theme.space_sm}>
              {field.state.value.map((filter, i) => {
                const dimensionValues =
                  props.dimensionValues[filter.schemaName] ?? [];

                const valueOptions = dimensionValues
                  .sort()
                  .map((value) => ({ label: value, value }));

                return (
                  <Flex key={i} alignItems="center" gap={theme.space_xs}>
                    <form.Field
                      name={`filters[${i}].schemaName`}
                      validators={{
                        onChange: props.required
                          ? z.string().min(1)
                          : z.string(),
                        onMount: props.required
                          ? z.string().min(1)
                          : z.string(),
                      }}
                      children={(subField) => {
                        const selectedValue =
                          filter.schemaName.length > 0
                            ? {
                                label: filter.displayName,
                                value: filter.schemaName,
                              }
                            : null;

                        return (
                          <Box width={250}>
                            <Select
                              isLoading={props.isLoadingDimensionPreferences}
                              hideSelectedOptions
                              options={groupedFilterOptions}
                              searchable
                              value={selectedValue}
                              onChange={(option) => {
                                if (option) {
                                  // First update the schemaName field to clear its validation
                                  subField.handleChange(option.value);

                                  // Then update the parent filter object
                                  field.replaceValue(i, {
                                    ...filter,
                                    displayName: option.label,
                                    schemaName: option.value,
                                    values: [],
                                  });

                                  props.onInteraction({
                                    type: BillingRuleForm.INTERACTION_UPDATE_DIMENSIONS,
                                    dimensionName: option.value,
                                  });
                                }
                              }}
                            />
                          </Box>
                        );
                      }}
                    />
                    <form.Field
                      name={`filters[${i}].operator`}
                      children={(subField) => {
                        const selectedOption = operatorOptions.find(
                          (option) => option.value === subField.state.value
                        );

                        return (
                          <Box width={202}>
                            <Select
                              options={operatorOptions}
                              value={selectedOption}
                              onChange={(option) => {
                                if (!option) return;

                                subField.handleChange(option.value);

                                if (
                                  option.value === Operator.NOT_SET ||
                                  option.value === Operator.SET
                                ) {
                                  form.setFieldMeta(
                                    `filters[${i}].values`,
                                    (meta) => ({
                                      ...meta,
                                      error: undefined,
                                      errorMap: {},
                                    })
                                  );
                                }
                              }}
                            />
                          </Box>
                        );
                      }}
                    />
                    <form.Subscribe
                      selector={(state) => state.values.filters[i].operator}
                      children={(operator) => {
                        if (
                          operator === Operator.NOT_SET ||
                          operator === Operator.SET
                        ) {
                          return null;
                        }

                        return (
                          <form.Field
                            name={`filters[${i}].values`}
                            validators={{
                              onChangeListenTo: [`filters[${i}].operator`],
                              onChange: ({ value, fieldApi }) => {
                                const operator = fieldApi.form.getFieldValue(
                                  `filters[${i}].operator`
                                );

                                if (
                                  operator === Operator.NOT_SET ||
                                  operator === Operator.SET
                                ) {
                                  return undefined;
                                }

                                if (props.required) {
                                  return value.length > 0
                                    ? undefined
                                    : "Please select at least one value";
                                }

                                return undefined;
                              },
                              onMount: props.required
                                ? z.array(z.string()).min(1)
                                : z.array(z.string()),
                            }}
                            children={(subField) => {
                              const isLoading =
                                subField.state.value &&
                                subField.state.value.length > 0
                                  ? false
                                  : props.isLoadingDimensionValues;

                              return (
                                <Box width={260}>
                                  <SelectCheckbox
                                    isCreatable={
                                      operator === Operator.CONTAINS ||
                                      operator === Operator.NOT_CONTAINS
                                    }
                                    isLoading={isLoading}
                                    options={valueOptions}
                                    selectedValues={filter.values ?? []}
                                    onChange={(values) =>
                                      subField.handleChange(values)
                                    }
                                  />
                                </Box>
                              );
                            }}
                          />
                        );
                      }}
                    />
                    <Button
                      iconEnd={<Icon icon={faTrash} />}
                      type="button"
                      size="small"
                      onClick={() => {
                        field.removeValue(i);
                      }}
                    />
                  </Flex>
                );
              })}
              <Button
                iconStart={<Icon icon={faPlus} />}
                size="tiny"
                type="button"
                width={100}
                onClick={() =>
                  field.pushValue({
                    displayName: "",
                    operator: Operator.EQUALS,
                    providerType: ProviderType.FOCUS,
                    schemaName: "",
                    values: [],
                  })
                }
              >
                {copyText.addFilterButtonLabel}
              </Button>
            </Flex>
          );
        }}
      />
    </Box>
  );
}
